import { Injectable } from "@angular/core";
import { PRIMARY_OUTLET, Router, UrlSegment, UrlSegmentGroup, UrlTree } from "@angular/router";
import { Contract } from "@edp-components/edp-contract";
import { ICustomUrl } from "@edp-models/deep-link.model";
import { EdpUser } from "@edp-models/user.model";
import { ApiService } from "@edp-services/api.service";
import { SetPaymentTab } from "@edp-store/actions/billing.actions";
import {
    GetContractFilters,
    HideContractSelection,
    IsSingleContract,
    SelectContract,
    SetContracts,
    SetUrlToRedirect,
    ShowContractSelection,
} from "@edp-store/actions/contract.actions";
import { SetDeepLink } from "@edp-store/actions/deep-link.actions";
import { SelectOverlayAction, SetTabMenu } from "@edp-store/actions/layout.actions";
import { getUser } from "@edp-store/selectors/user.selectors";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { combineLatest } from "rxjs";
import { take } from "rxjs/operators";
import { AuthenticationService } from "./authentication.service";
import { ContractService } from "./contract.service";
import { PaymentMethodEnum } from "@edp-enums/payment-method.enum";

const keyAnalyticsLocalStorage = "analytics_queryparams";

@Injectable({
    providedIn: "root",
})
export class DeepLinkService {
    constructor(
        private apiService: ApiService,
        private store: Store<IAppState>,
        private router: Router,
        private authService: AuthenticationService,
        private contractService: ContractService
    ) {}

    public getAll() {
        return this.apiService.callServicePostToCollection("deepLink.getAll", null);
    }

    private checkParamContractId(deepLink: ICustomUrl, redirectLink: string): boolean {
        return deepLink.link.includes("contract_id") && deepLink.link.split("/")[0] === redirectLink.split("/")[0];
    }

    public getConfiguredLink(deepLinksList: ICustomUrl[], redirectLink: string): ICustomUrl {
        return deepLinksList.find(item => redirectLink === item.link || this.checkParamContractId(item, redirectLink));
    }

    public redirectToDefaultRoute() {
        this.store.dispatch(new SetUrlToRedirect(null));
        this.router.navigate(["/energia/inicio"], { queryParams: this.getAnalyticsQueryParams(), queryParamsHandling: "merge" });
    }

    private setContractAndRedirect(link, contract, user: EdpUser): void {
        this.store.dispatch(new SelectContract(contract));
        this.store.dispatch(new HideContractSelection());
        this.redirectToDeepLinkRouteWithoutContract(link, contract, user);
    }

    public checkRouteTypeAndRedirect(link: ICustomUrl, contract?: Contract, currentUrl?: UrlSegment[]) {
        this.store.dispatch(new SetDeepLink(link));
        const user$ = this.store.pipe(take(1), select(getUser));
        const contracts$ = this.contractService.getList().pipe(take(1));

        combineLatest([user$, contracts$]).subscribe(([user, contracts]: [EdpUser, Contract[]]) => {
            const contractsList = contracts.map(contractItem => this.contractService.createContract(contractItem));

            link = { ...link, redirectInProgress: true };
            this.store.dispatch(new SetUrlToRedirect(link));

            if (link.link.includes("contract_id") && currentUrl?.length > 0) {
                const contractIdParm = currentUrl[1]?.path;
                if (!contractIdParm && contractsList.filter(i => i.isActivo()).length > 1) {
                    this.checkAccess4Contract(link, user, contractsList);
                    return;
                }

                const contract = !!contractIdParm
                    ? contractsList.find(contractItem => contractItem.ContractId === contractIdParm)
                    : contractsList.find(contract => contract.isActivo());
                this.setContractAndRedirect(link, contract, user);
                return;
            }

            if (link.contractNeeded === "0") {
                const firstContract = contractsList.find(contractItem => contractItem.isActivo());
                this.setContractAndRedirect(link, !!contract ? contract : firstContract, user);
                return;
            }

            if (contract) {
                this.store.dispatch(new HideContractSelection());

                this.redirectToDeepLinkRouteWithContract(link, contract.ContractId);
                return;
            }

            this.checkAccess4Contract(link, user, contractsList);
        });
    }

    private checkAccess4Contract(configuredLink: ICustomUrl, user: EdpUser, contractsList: Contract[]) {
        this.store.dispatch(new SetContracts(contractsList));
        this.store.dispatch(new GetContractFilters());

        const activeContracts = contractsList.filter(contract => contract.isActivo());
        let counter = 0;
        let selectedContract: Contract;

        activeContracts.forEach(item => {
            if (this.authService.hasAccess(configuredLink.ruleType, item, user)) {
                counter++;
                selectedContract = item;
            }
        });

        if (contractsList.length > 1) {
            this.store.dispatch(new IsSingleContract(false));
        }

        if (counter > 1) {
            this.store.dispatch(new ShowContractSelection());

            if (!!configuredLink && !!configuredLink.acronym) {
                const type = configuredLink.acronym.includes("LOCAL") ? "LOCAL" : configuredLink.acronym;
                switch (type) {
                    case "OPENBANKING_TAB":
                        this.store.dispatch(new SetPaymentTab(PaymentMethodEnum.OPEN_BANKING));
                        return;
                    case "MBWAY_TAB":
                        this.store.dispatch(new SetPaymentTab(PaymentMethodEnum.MB_WAY));
                        return;
                    case "LOCAL":
                        localStorage.setItem("local-deeplink", configuredLink.acronym);
                        return;
                    case "MONTH_PAYMENT_CC":
                        localStorage.setItem("conta-certa-deeplink", configuredLink.acronym);
                        return;
                    default:
                        return;
                }
            }

            if (!!configuredLink && !!configuredLink.page) {
                const page = configuredLink.page;
                switch (page) {
                    case "pack-simulador":
                        return;
                    case "novo-plano":
                        return;
                    default:
                        break;
                }
            }
        }

        if (counter === 1) {
            if (!!configuredLink && configuredLink.acronym === "OPENBANKING_TAB") {
                this.store.dispatch(new SetPaymentTab(PaymentMethodEnum.OPEN_BANKING));
            }

            if (!!configuredLink && configuredLink.acronym === "MBWAY_TAB") {
                this.store.dispatch(new SetPaymentTab(PaymentMethodEnum.MB_WAY));
            }

            if (!!configuredLink && configuredLink.acronym === "MONTH_PAYMENT_CC") {
                localStorage.setItem("conta-certa-deeplink", configuredLink.acronym);
            }
        }

        if (counter < 1) {
            this.redirectToDefaultRoute();
            return;
        }

        const isLocalDeeplink = !!configuredLink && !!configuredLink.acronym?.includes("LOCAL");
        const hasContractNeedSelection = activeContracts.length > 1 && !isLocalDeeplink;
        if (hasContractNeedSelection) {
            return;
        }

        this.store.dispatch(new SelectContract(selectedContract));
        this.store.dispatch(new HideContractSelection());
        isLocalDeeplink
            ? this.redirectToDeepLinkRouteWithoutContract(configuredLink, selectedContract, user)
            : this.redirectToDeepLinkRouteWithContract(configuredLink, selectedContract.ContractId);
    }

    private redirectToDeepLinkRouteWithContract(configuredLink: ICustomUrl, contractId: string): void {
        const url = configuredLink.url.replace(":contractId", contractId);
        const route = configuredLink.acronym?.includes("DEFAULT") ? url : `energia/${contractId}/${configuredLink.url}`;
        this.router.navigate([route], { queryParamsHandling: "merge" });
    }

    private createUrlTree(url: Array<string>, defaultQueryParams: object = {}): UrlTree {
        const queryParams = !!this.hasAnalyticsParamsToSet()
            ? { ...defaultQueryParams, ...this.getAnalyticsQueryParams() }
            : { ...defaultQueryParams };
        return this.router.createUrlTree(url, { queryParamsHandling: "merge", queryParams });
    }

    private redirectToDeepLinkRouteWithoutContract(link: ICustomUrl, contract: Contract, user: EdpUser) {
        if (link.url.indexOf("http") > -1) {
            window.location.href = `${link.url}`;
            return;
        }

        this.store.dispatch(new HideContractSelection());

        if (this.authService.hasAccess(link.ruleType, contract, user)) {
            let tree: UrlTree;
            switch (link.acronym) {
                case "INDIVIDUAL_CHALLENGE":
                    tree = this.createUrlTree([link.url + "/desafios-individuais"]);
                    break;
                case "COLLECTIVE_CHALLENGE":
                    tree = this.createUrlTree([link.url + "/desafio-global"], { eventCode: link.eventCode });
                    break;
                case "EXPERIENCE":
                    tree = this.createUrlTree([link.url + "/experiencias"], { eventCode: link.eventCode });
                    break;
                case "DRAW":
                    tree = this.createUrlTree([link.url], { Type: "Sorteio a decorrer" });
                    break;
                case "INVOICE_DISCOUNT":
                    tree = this.createUrlTree([link.url], { Type: "Desconto em fatura" });
                    break;
                case "PARTNER":
                    tree = this.createUrlTree([link.url], { Type: "Ofertas em parceiro" });
                    break;
                case "MGM_SIDEBAR":
                    tree = this.createUrlTree([link.url]);
                    sessionStorage.setItem("mgm", "planeta");
                    this.store.dispatch(new SelectOverlayAction("mgm"));
                    break;
                case "NOTIFICATION_PLANET_Z":
                    tree = this.createUrlTree(["planeta-zero/inicio"]);
                    this.store.dispatch(new SelectOverlayAction("notifications"));
                    this.store.dispatch(new SetTabMenu("APPE"));
                    break;
                case "NOTIFICATION_CONTRACT":
                    this.store.dispatch(new SetTabMenu("CONTRACT"));
                    tree = this.createUrlTree([link.url]);
                    break;
                case "NOTIFICATION_ALL":
                    this.store.dispatch(new SetTabMenu("ALL"));
                    tree = this.createUrlTree([link.url]);
                    break;
                case "COMMUNICATIONS_CONSENTS":
                    tree = this.createUrlTree([!!contract ? `energia/${contract.ContractId}/inicio` : link.url]);
                    sessionStorage.setItem("profileCC", "cc");
                    this.store.dispatch(new SelectOverlayAction("user"));
                    break;
                default:
                    tree = this.createUrlTree([link.url]);
                    break;
            }

            link.acronym?.includes("LOCAL")
                ? this.setNewLocalDeeplink(link.acronym, contract.ContractId)
                : this.router.navigate(this.getTreeSegments(tree), { queryParams: tree.queryParams });
            return;
        }

        this.redirectToDefaultRoute();
    }

    private getTreeSegments(tree: UrlTree): string[] {
        const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
        const rst: string[] = [];
        g.segments.forEach(segment => rst.push(segment.path));
        return rst;
    }

    public navigate(url: Array<string>, defaultQueryParams: object = {}): void {
        const queryParams = !!this.hasAnalyticsParamsToSet()
            ? { ...defaultQueryParams, ...this.getAnalyticsQueryParams() }
            : { ...defaultQueryParams };
        this.router.navigate(url, queryParams ? { queryParams } : {});
    }

    public setNewLocalDeeplink(acronym, contractId) {
        switch (acronym) {
            case "LOCAL_HOURLY_OPTION":
                this.navigate([`/locais-consumo/${contractId}/opcao-horaria`]);
                break;
            case "LOCAL_POWER":
                this.navigate([`/locais-consumo/${contractId}/potencia`]);
                break;
            case "LOCAL_HOME":
                this.navigate([`/locais-consumo/${contractId}/casa`]);
                break;
            case "LOCAL_HOME_SPECS":
                this.navigate([`/locais-consumo/${contractId}/casa/alteracao`]);
                break;
            case "LOCAL_PAYMENT":
                this.navigate([`/locais-consumo/${contractId}/pagamento`]);
                break;
            case "LOCAL_PAYMENT_MOD":
                this.navigate([`/locais-consumo/${contractId}/pagamento/alteracao`]);
                break;
            case "LOCAL_PAYMENT_COND":
                this.navigate([`/locais-consumo/${contractId}/pagamento/condicoes`]);
                break;
            case "LOCAL_PHONE_UPDATE":
                localStorage.setItem("local-section", "LOCAL_PHONE_UPDATE");
                this.navigate([`/locais-consumo/${contractId}/titular-contrato`]);
                break;
            case "LOCAL_ADD_PARTY":
                localStorage.setItem("local-section", "LOCAL_ADD_PARTY");
                this.navigate([`/locais-consumo/${contractId}/inicio`]);
                break;
            case "LOCAL":
            case "LOCAL_SERVICES":
            case "LOCAL_SERVICES_ADDED":
            default:
                this.navigate([`/locais-consumo/${contractId}/inicio`]);
                break;
        }

        if (acronym === "LOCAL_SERVICES" || acronym === "LOCAL_SERVICES_ADDED") {
            localStorage.setItem("local-section", acronym);
        }
    }

    public getAnalyticsQueryParams() {
        const queryParams = JSON.parse(localStorage.getItem(keyAnalyticsLocalStorage));
        localStorage.removeItem(keyAnalyticsLocalStorage);
        return queryParams ? queryParams : {};
    }

    public hasAnalyticsParamsToSet(): boolean {
        return !!localStorage.getItem(keyAnalyticsLocalStorage);
    }
}
